import * as React from 'react'
import { FunctionComponent, useContext } from 'react'
import {
    ActionButton,
    DirectionalHint,
    getTheme,
    Icon,
    mergeStyleSets,
    Stack,
    TeachingBubble,
    Text,
} from '@fluentui/react'
import { mergeStyles } from '@fluentui/react/lib/Styling'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../reducers'
import { getHammockIconProps } from '../../helpers/iconHelper'
import { AppAction } from '../../config/appConfig'
import { IAppState } from '../../reducers/appReducer'
import { useBoolean, useMount } from '@fluentui/react-hooks'
import useHost from '../../hooks/useHost'
import IdentityContext from '../../context/IdentityContext'
import { useTranslation } from 'react-i18next'

export type Build = {
    name: string
    version: string
    timestamp: bigint
    author: string
}

const theme = getTheme()
const classNames = mergeStyleSets({
    whiteText: mergeStyles({ color: theme.palette.white }),
    lightText: mergeStyles({ color: theme.palette.themeLighterAlt }),
})

type BubbleProps = { toggleBubble: () => void }

const Bubble: FunctionComponent<BubbleProps> = props => {
    const { t: translate } = useTranslation()
    const version = (process.env.BUILD as unknown as Build).version

    return (
        <TeachingBubble
            target="#versionNumber"
            calloutProps={{
                directionalHint: DirectionalHint.topRightEdge,
            }}
            primaryButtonProps={{
                text: translate('release.notes'),
                iconProps: { iconName: 'MSNVideosSolid' },
                onClick: () => {
                    props.toggleBubble()
                    window.open(process.env.RELEASE_NOTES_URL, '_blank')
                },
            }}
            secondaryButtonProps={{
                text: translate('action.close'),
                iconProps: { iconName: 'Completed', className: classNames.lightText },
                onClick: () => props.toggleBubble(),
            }}
            headline={translate('release.title')}
        >
            <Stack tokens={{ childrenGap: 's2' }}>
                <Text block className={classNames.lightText}>
                    {translate('release.version', { version: version })}{' '}
                    <Icon iconName="GiftboxOpen" className={classNames.whiteText} />.
                </Text>
                <Text block className={classNames.lightText}>
                    {translate('release.view')}
                </Text>
            </Stack>
        </TeachingBubble>
    )
}

const Version: FunctionComponent = () => {
    const app = useSelector<Store, IAppState>(state => state.app)
    const { authenticated } = useContext(IdentityContext)
    const host = useHost()
    const dispatch = useDispatch()
    const version = (process.env.BUILD as unknown as Build).version
    const [showBubble, { toggle: toggleBubble }] = useBoolean(false)

    useMount(() => {
        if (!app.version || app.version !== version) {
            // Log the app out so that we know we sure that the app state is renewed.
            dispatch({ type: AppAction.SetVersion, payload: version })
        }
    })

    return (
        <>
            <ActionButton
                id="versionNumber"
                onClick={toggleBubble}
                iconProps={getHammockIconProps()}
                styles={{ flexContainer: { flexDirection: 'row-reverse' } }}
            >
                {`${host?.Name ?? ''} v${version}`}
            </ActionButton>
            {authenticated && showBubble && <Bubble toggleBubble={toggleBubble} />}
        </>
    )
}

export default Version
